import { createRouter, createWebHashHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useRankStore } from '@/stores/rank'
import { ElMessage } from 'element-plus'
import { ADMIN_ENTRANCE_KEY, SITE_TITLE_SUFFIX } from '@/config/constants'
import Challenges from '@/views/user/Challenges.vue'
import KbIndex from '@/views/kb/Index.vue'
import SeriesList from '@/views/kb/SeriesList.vue'
import { startKbSeriesListPrefetch } from '@/utils/kbSeriesListPrefetch'

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/Login.vue'),
    meta: { requiresAuth: false, title: '登录' }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: () => import('@/views/ForgotPassword.vue'),
    meta: { requiresAuth: false, title: '忘记密码' }
  },
  {
    path: '/',
    redirect: '/challenges'
  },
  {
    path: '/challenges',
    name: 'Challenges',
    component: Challenges,
    meta: { requiresAuth: true, title: '挑战训练场' }
  },
  {
    path: '/challenges/:id',
    name: 'ChallengeDetail',
    component: () => import('@/views/user/ChallengeDetail.vue'),
    meta: { requiresAuth: true, title: '挑战详情' }
  },
  {
    path: '/attempts/:id',
    name: 'Attempt',
    component: () => import('@/views/user/Attempt.vue'),
    meta: { requiresAuth: true, title: '提交记录' }
  },
  {
    path: '/leaderboard',
    name: 'Leaderboard',
    component: () => import('@/views/user/Leaderboard.vue'),
    meta: { requiresAuth: true, title: '排行榜' }
  },
  {
    path: '/me/achievements',
    name: 'Achievements',
    component: () => import('@/views/user/Achievements.vue'),
    meta: { requiresAuth: true, title: '我的成就' }
  },
  {
    path: '/me/profile',
    name: 'Profile',
    component: () => import('@/views/user/Profile.vue'),
    meta: { requiresAuth: true, title: '个人主页' }
  },
  {
    path: '/me/friends',
    name: 'Friends',
    component: () => import('@/views/user/Friends.vue'),
    meta: { requiresAuth: true, title: '好友聊天' }
  },
  {
    path: '/me/notifications',
    name: 'Notifications',
    component: () => import('@/views/user/Notifications.vue'),
    meta: { requiresAuth: true, title: '通知中心' }
  },
  {
    path: '/me/favorites',
    name: 'MyFavorites',
    component: () => import('@/views/user/MyFavorites.vue'),
    meta: { requiresAuth: true, title: '我的收藏' }
  },
  {
    path: '/me/avatar-frames',
    name: 'AvatarFrameManage',
    component: () => import('@/views/user/AvatarFrameManage.vue'),
    meta: { requiresAuth: true, title: '我的装扮' }
  },
  {
    path: '/sponsor',
    name: 'Sponsor',
    component: () => import('@/views/Sponsor.vue'),
    meta: { requiresAuth: true, title: '赞助一下' }
  },
  {
    path: '/me/feedback',
    name: 'Feedback',
    component: () => import('@/views/user/Feedback.vue'),
    meta: { requiresAuth: true, title: '意见反馈' }
  },
  {
    path: '/learning/resources/:id',
    name: 'ResourceDetail',
    component: () => import('@/views/user/ResourceDetail.vue'),
    meta: { requiresAuth: true, title: '学习资料' }
  },
  {
    path: '/admin',
    redirect: '/admin/dashboard'
  },
  {
    path: '/admin/dashboard',
    name: 'AdminDashboard',
    component: () => import('@/views/admin/Dashboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '概览' }
  },
  {
    path: '/admin/entrance',
    name: 'AdminAuth',
    component: () => import('@/views/admin/AdminAuth.vue'),
    meta: { requiresAuth: false, title: '管理员登录' }
  },
  {
    path: '/admin/invites',
    name: 'AdminInvites',
    component: () => import('@/views/admin/AdminInvites.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '管理员邀请码' }
  },
  {
    path: '/admin/challenges',
    name: 'AdminChallenges',
    component: () => import('@/views/admin/Challenges.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '挑战管理' }
  },
  {
    path: '/admin/announcements',
    name: 'AdminAnnouncements',
    component: () => import('@/views/admin/Announcements.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '公告管理' }
  },
  {
    path: '/admin/tags',
    name: 'AdminTags',
    component: () => import('@/views/admin/Tags.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '标签管理' }
  },
  {
    path: '/admin/learning',
    name: 'AdminLearning',
    component: () => import('@/views/admin/Learning.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '参考资料' }
  },
  {
    path: '/admin/judge-jobs',
    name: 'AdminJudgeJobs',
    component: () => import('@/views/admin/JudgeJobs.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '评测队列' }
  },
  {
    path: '/admin/submissions',
    name: 'AdminSubmissions',
    component: () => import('@/views/admin/Submissions.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '提交管理' }
  },
  {
    path: '/kb',
    name: 'KnowledgeBase',
    component: KbIndex,
    meta: { requiresAuth: true, title: '知识库' }
  },
  // 分类下专题列表（从 /kb 点击分类进入）
  {
    path: '/kb/series',
    name: 'KbSeriesList',
    component: SeriesList,
    meta: { requiresAuth: true, title: '专题列表' }
  },
  {
    path: '/kb/series/:id',
    name: 'KbSeries',
    component: () => import('@/views/kb/Series.vue'),
    meta: { requiresAuth: true, title: '专题详情' }
  },
  {
    path: '/kb/articles/:id',
    name: 'KbArticle',
    component: () => import('@/views/kb/Article.vue'),
    meta: { requiresAuth: true, title: '文章详情' }
  },
  {
    path: '/admin/kb/categories',
    name: 'AdminKbCategories',
    component: () => import('@/views/admin/kb/Categories.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '分类管理' }
  },
  {
    path: '/admin/kb/series',
    name: 'AdminKbSeries',
    component: () => import('@/views/admin/kb/Series.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '专题管理' }
  },
  {
    path: '/admin/kb/articles',
    name: 'AdminKbArticles',
    component: () => import('@/views/admin/kb/Articles.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '文章管理' }
  },
  {
    path: '/admin/feedback',
    name: 'AdminFeedback',
    component: () => import('@/views/admin/Feedback.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '反馈管理' }
  },
  {
    path: '/admin/leaderboard',
    name: 'AdminLeaderboard',
    component: () => import('@/views/admin/Leaderboard.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '排行榜管理' }
  },
  {
    path: '/admin/audit-logs',
    name: 'AdminAuditLogs',
    component: () => import('@/views/admin/AuditLogs.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '操作日志' }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('@/views/admin/Users.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '用户管理' }
  },
  {
    path: '/admin/suspicious-users',
    name: 'AdminSuspiciousUsers',
    component: () => import('@/views/admin/SuspiciousUsers.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '嫌疑用户' }
  },
  {
    path: '/admin/shop',
    name: 'AdminShop',
    component: () => import('@/views/admin/Shop.vue'),
    meta: { requiresAuth: true, requiresAdmin: true, title: '商城管理' }
  },
  {
    path: '/shop',
    name: 'Shop',
    component: () => import('@/views/shop/Index.vue'),
    meta: { requiresAuth: false, title: '积分商城' }
  },
  {
    path: '/shop/items/:id',
    name: 'ShopItemDetail',
    component: () => import('@/views/shop/ItemDetail.vue'),
    meta: { requiresAuth: false, title: '商品详情' }
  },
  {
    path: '/shop/me/purchases',
    name: 'MyPurchases',
    component: () => import('@/views/shop/MyPurchases.vue'),
    meta: { requiresAuth: true, title: '我的兑换记录' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: { requiresAuth: false, title: '页面未找到' }
  }
]

const router = createRouter({
  // 使用 hash 模式，避免部署端未配置 try_files 导致刷新 404
  // 如需恢复 history 模式，需要 Nginx 配置：try_files $uri $uri/ /index.html;
  history: createWebHashHistory(),
  routes
})

router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // 隐藏管理员入口：需要携带随机口令
  if (to.path === '/admin/entrance') {
    const k = typeof to.query.k === 'string' ? to.query.k : ''
    if (k !== ADMIN_ENTRANCE_KEY) {
      next('/login')
      return
    }
  }

  // 需要登录的路由：无 token 直接去登录
  if (to.meta.requiresAuth) {
    if (!authStore.token) {
      next('/login')
      return
    }

    // 有 token 但 user 未加载时，主动校验一次（防止 localStorage 里存了 "undefined"/过期 token 导致“免登录进入”）
    if (!authStore.user) {
      try {
        await authStore.getCurrentUser()
      } catch (e) {
        next('/login')
        return
      }
    }
  }

  // 积分商城：已登录用户需达到白银段位方可进入（防止直接输入地址或刷新进入）
  // 提示仅在导航栏点击时显示，此处只做重定向，避免与点击处理重复弹两次
  if (to.path === '/shop' && authStore.token) {
    const rankStore = useRankStore()
    await rankStore.ensureLoaded()
    if (!rankStore.isAtLeastSilver) {
      next('/challenges')
      return
    }
  }

  // 进入专题列表页时尽早发起接口预取，改善 LCP
  if (to.name === 'KbSeriesList' && to.query.categoryId) {
    startKbSeriesListPrefetch(to.query.categoryId)
  }

  if (to.meta.requiresAdmin) {
    // 检查是否是管理员
    const isAdmin = authStore.isAdmin()
    const isSuperAdmin = authStore.isSuperAdmin()
    
    if (!isAdmin && !isSuperAdmin) {
      // 开发环境输出调试信息
      if (process.env.NODE_ENV === 'development') {
        console.warn('[Router] 访问管理后台被拒绝:', {
          path: to.path,
          username: authStore.user?.username,
          role: authStore.user?.role,
          isAdmin,
          isSuperAdmin
        })
      }
      
      // 提示用户权限不足
      ElMessage.warning('您没有访问管理后台的权限')
      next('/challenges')
      return
    }
  }

  // 设置页面标题
  document.title = to.meta.title ? `${to.meta.title} - ${SITE_TITLE_SUFFIX}` : SITE_TITLE_SUFFIX

  next()
})

export default router
